import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Users, DollarSign, PiggyBank, Receipt, Save, TrendingUp, Building2, Percent } from 'lucide-react';
import axios from 'axios';
import { formatCurrency } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

// ==================== CANADIAN TAX CALCULATOR ====================

const FEDERAL_BRACKETS = [
  { limit: 55867, rate: 0.15 },
  { limit: 111733, rate: 0.205 },
  { limit: 154906, rate: 0.26 },
  { limit: 220000, rate: 0.29 },
  { limit: Infinity, rate: 0.33 },
];
const FEDERAL_BPA = 15705;

const MB_BRACKETS = [
  { limit: 47000, rate: 0.108 },
  { limit: 100000, rate: 0.1275 },
  { limit: Infinity, rate: 0.174 },
];
const MB_BPA = 15000;

function calcBracketTax(income, brackets, bpa) {
  const taxable = Math.max(income - bpa, 0);
  let tax = 0, prev = 0;
  for (const b of brackets) {
    const chunk = Math.min(taxable, b.limit) - prev;
    if (chunk <= 0) break;
    tax += chunk * b.rate;
    prev = b.limit;
  }
  return Math.max(tax, 0);
}

function estimateTax(commissionT4A, dividendT5) {
  // T4A commission is regular income
  const fedTaxComm = calcBracketTax(commissionT4A, FEDERAL_BRACKETS, FEDERAL_BPA);
  const mbTaxComm = calcBracketTax(commissionT4A, MB_BRACKETS, MB_BPA);
  const cppOnComm = Math.min(commissionT4A * 0.1190 / 2, 3867); // CPP employee portion estimate

  // Eligible dividends: gross-up 38%, federal DTC 15.0198%, MB DTC 8%
  const grossedUp = dividendT5 * 1.38;
  const fedTaxDiv = calcBracketTax(commissionT4A + grossedUp, FEDERAL_BRACKETS, FEDERAL_BPA) - fedTaxComm;
  const mbTaxDiv = calcBracketTax(commissionT4A + grossedUp, MB_BRACKETS, MB_BPA) - mbTaxComm;
  const fedDTC = grossedUp * 0.150198;
  const mbDTC = grossedUp * 0.08;
  const netDivTax = Math.max(fedTaxDiv + mbTaxDiv - fedDTC - mbDTC, 0);

  const totalPersonalTax = fedTaxComm + mbTaxComm + cppOnComm + netDivTax;
  const effectiveRate = (commissionT4A + dividendT5) > 0
    ? (totalPersonalTax / (commissionT4A + dividendT5) * 100) : 0;

  return {
    commission: { federal: fedTaxComm, provincial: mbTaxComm, cpp: cppOnComm, total: fedTaxComm + mbTaxComm + cppOnComm },
    dividend: { grossedUp, fedDTC, mbDTC, netTax: netDivTax },
    totalPersonalTax,
    effectiveRate,
  };
}

function estimateCorporateTax(retained) {
  // CCPC small business rate: 9% federal + 0% Manitoba = 9%
  const rate = 0.09;
  return { tax: retained * rate, rate: rate * 100, afterTax: retained * (1 - rate) };
}

// ==================== OWNER ALLOCATION CARD ====================

function OwnerCard({ owner, onUpdate }) {
  const total = owner.total_earnings;
  const [commPct, setCommPct] = useState(
    total > 0 ? Math.round((owner.allocated_commission / total) * 100) : 0
  );
  const [divPct, setDivPct] = useState(
    total > 0 ? Math.round((owner.allocated_dividend / total) * 100) : 0
  );

  const commAmt = Math.round(total * commPct / 100 * 100) / 100;
  const divAmt = Math.round(total * divPct / 100 * 100) / 100;
  const retainedAmt = Math.round((total - commAmt - divAmt) * 100) / 100;
  const retainedPct = 100 - commPct - divPct;

  const tax = estimateTax(commAmt, divAmt);
  const corpTax = estimateCorporateTax(retainedAmt);
  const totalTaxBurden = tax.totalPersonalTax + corpTax.tax;
  const takeHome = commAmt + divAmt - tax.totalPersonalTax;

  const handleCommSlider = (val) => {
    const v = val[0];
    const maxComm = 100 - divPct;
    const clamped = Math.min(v, maxComm);
    setCommPct(clamped);
    onUpdate(owner.mortgage_agent_id, total, Math.round(total * clamped / 100 * 100) / 100, divAmt);
  };

  const handleDivSlider = (val) => {
    const v = val[0];
    const maxDiv = 100 - commPct;
    const clamped = Math.min(v, maxDiv);
    setDivPct(clamped);
    onUpdate(owner.mortgage_agent_id, total, commAmt, Math.round(total * clamped / 100 * 100) / 100);
  };

  return (
    <Card className="border-2 border-slate-200" data-testid={`owner-card-${owner.name.toLowerCase().replace(' ', '-')}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{owner.name}</CardTitle>
            <CardDescription className="flex gap-4 mt-1">
              <span>Mortgage: {formatCurrency(owner.mortgage_earnings)}</span>
              <span>Insurance: {formatCurrency(owner.insurance_earnings)}</span>
            </CardDescription>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase">Total Earnings</p>
            <p className="text-2xl font-bold font-mono text-blue-700">{formatCurrency(total)}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Allocation Sliders */}
        <div className="space-y-5">
          {/* Commission T4A */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-2 text-amber-700 font-semibold">
                <Receipt className="w-4 h-4" /> Commission (T4A)
              </Label>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-amber-700">{commPct}%</Badge>
                <span className="font-mono font-bold text-amber-700">{formatCurrency(commAmt)}</span>
              </div>
            </div>
            <Slider
              value={[commPct]}
              max={100}
              step={1}
              onValueChange={handleCommSlider}
              className="[&_[role=slider]]:bg-amber-500 [&_[role=slider]]:border-amber-600"
              data-testid={`slider-comm-${owner.name.toLowerCase().replace(' ', '-')}`}
            />
          </div>

          {/* Dividend T5 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-2 text-green-700 font-semibold">
                <PiggyBank className="w-4 h-4" /> Dividend (T5)
              </Label>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-green-700">{divPct}%</Badge>
                <span className="font-mono font-bold text-green-700">{formatCurrency(divAmt)}</span>
              </div>
            </div>
            <Slider
              value={[divPct]}
              max={100}
              step={1}
              onValueChange={handleDivSlider}
              className="[&_[role=slider]]:bg-green-500 [&_[role=slider]]:border-green-600"
              data-testid={`slider-div-${owner.name.toLowerCase().replace(' ', '-')}`}
            />
          </div>

          {/* Retained (read-only) */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2 text-slate-600 font-semibold">
                <Building2 className="w-4 h-4" /> Retained in Corporation
              </Label>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-slate-600">{retainedPct}%</Badge>
                <span className="font-mono font-bold text-slate-700">{formatCurrency(retainedAmt)}</span>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Tax Impact Estimates */}
        <div>
          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Percent className="w-4 h-4" /> Estimated Tax Impact
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
              <p className="text-xs text-amber-600 uppercase mb-1">Personal Tax on T4A</p>
              <p className="font-mono font-bold text-amber-800">{formatCurrency(tax.commission.total)}</p>
              <p className="text-xs text-amber-500 mt-1">
                Fed: {formatCurrency(tax.commission.federal)} + MB: {formatCurrency(tax.commission.provincial)} + CPP: {formatCurrency(tax.commission.cpp)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 border border-green-100">
              <p className="text-xs text-green-600 uppercase mb-1">Personal Tax on Dividend</p>
              <p className="font-mono font-bold text-green-800">{formatCurrency(tax.dividend.netTax)}</p>
              <p className="text-xs text-green-500 mt-1">
                After DTC credits ({formatCurrency(tax.dividend.fedDTC + tax.dividend.mbDTC)})
              </p>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <p className="text-xs text-slate-500 uppercase mb-1">Corporate Tax on Retained</p>
              <p className="font-mono font-bold text-slate-700">{formatCurrency(corpTax.tax)}</p>
              <p className="text-xs text-slate-400 mt-1">SBD rate: {corpTax.rate}% CCPC</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
              <p className="text-xs text-blue-600 uppercase mb-1">Est. Take-Home</p>
              <p className="font-mono font-bold text-blue-800">{formatCurrency(takeHome)}</p>
              <p className="text-xs text-blue-500 mt-1">
                Effective rate: {tax.effectiveRate.toFixed(1)}%
              </p>
            </div>
          </div>
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-100 flex justify-between items-center">
            <span className="text-sm font-semibold text-red-700">Total Estimated Tax Burden</span>
            <span className="font-mono font-bold text-red-800 text-lg">{formatCurrency(totalTaxBurden)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== MAIN PLANNER COMPONENT ====================

export default function ShareholderPlanner({ fiscalYear, loadData }) {
  const [earningsSummary, setEarningsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allocations, setAllocations] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${API}/api/accounting/shareholders/earnings-summary?fiscal_year=${fiscalYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEarningsSummary(res.data);
      const initial = {};
      for (const o of res.data.owners) {
        initial[o.mortgage_agent_id] = {
          total: o.total_earnings,
          commission: o.allocated_commission,
          dividend: o.allocated_dividend,
        };
      }
      setAllocations(initial);
    } catch {
      toast.error('Failed to load shareholder earnings');
    } finally {
      setLoading(false);
    }
  }, [fiscalYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdate = (agentId, total, commission, dividend) => {
    setAllocations(prev => ({ ...prev, [agentId]: { total, commission, dividend } }));
  };

  const saveAllocations = async () => {
    setSaving(true);
    const token = getToken();
    try {
      for (const owner of earningsSummary.owners) {
        const alloc = allocations[owner.mortgage_agent_id];
        if (!alloc) continue;
        await axios.post(
          `${API}/api/accounting/shareholders/allocations`,
          {
            agent_id: owner.mortgage_agent_id,
            agent_name: owner.name,
            fiscal_year: fiscalYear,
            total_earnings: alloc.total,
            commission_amount: alloc.commission,
            dividend_amount: alloc.dividend,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success('Shareholder allocations saved successfully');
      if (loadData) loadData();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save allocations');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }

  if (!earningsSummary) return null;

  const totalComm = Object.values(allocations).reduce((s, a) => s + (a.commission || 0), 0);
  const totalDiv = Object.values(allocations).reduce((s, a) => s + (a.dividend || 0), 0);
  const totalRetained = earningsSummary.net_brokerage_earnings - totalComm - totalDiv;
  const corpTaxOnRetained = estimateCorporateTax(totalRetained);

  return (
    <div className="space-y-6" data-testid="shareholder-planner">
      {/* Header Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" /> Shareholder Allocation Planner
          </CardTitle>
          <CardDescription>
            Allocate owner earnings between Commission (T4A), Dividend (T5), and Retained Earnings for {fiscalYear}. Adjust sliders to see real-time tax impact.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-xs text-blue-600 uppercase">Net Brokerage Earnings</p>
              <p className="text-xl font-bold font-mono text-blue-700">{formatCurrency(earningsSummary.net_brokerage_earnings)}</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg text-center">
              <p className="text-xs text-amber-600 uppercase">Commission (T4A)</p>
              <p className="text-xl font-bold font-mono text-amber-700">{formatCurrency(totalComm)}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-xs text-green-600 uppercase">Dividend (T5)</p>
              <p className="text-xl font-bold font-mono text-green-700">{formatCurrency(totalDiv)}</p>
            </div>
            <div className="p-4 bg-slate-100 rounded-lg text-center">
              <p className="text-xs text-slate-500 uppercase">Retained Earnings</p>
              <p className="text-xl font-bold font-mono text-slate-700">{formatCurrency(totalRetained)}</p>
              <p className="text-xs text-slate-400 mt-1">Corp tax: {formatCurrency(corpTaxOnRetained.tax)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Owner Cards */}
      {earningsSummary.owners.map(owner => (
        <OwnerCard key={owner.mortgage_agent_id} owner={owner} onUpdate={handleUpdate} />
      ))}

      {/* Combined Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5" /> Combined Tax Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Owner</TableHead>
                <TableHead className="text-right">Total Earnings</TableHead>
                <TableHead className="text-right">Commission (T4A)</TableHead>
                <TableHead className="text-right">Dividend (T5)</TableHead>
                <TableHead className="text-right">Retained</TableHead>
                <TableHead className="text-right">Est. Personal Tax</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {earningsSummary.owners.map(owner => {
                const a = allocations[owner.mortgage_agent_id] || {};
                const comm = a.commission || 0;
                const div = a.dividend || 0;
                const ret = owner.total_earnings - comm - div;
                const tax = estimateTax(comm, div);
                return (
                  <TableRow key={owner.mortgage_agent_id}>
                    <TableCell className="font-medium">{owner.name}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(owner.total_earnings)}</TableCell>
                    <TableCell className="text-right font-mono text-amber-700">{formatCurrency(comm)}</TableCell>
                    <TableCell className="text-right font-mono text-green-700">{formatCurrency(div)}</TableCell>
                    <TableCell className="text-right font-mono text-slate-600">{formatCurrency(ret)}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{formatCurrency(tax.totalPersonalTax)}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow className="bg-slate-50 font-bold">
                <TableCell>Total</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(earningsSummary.total_owner_earnings)}</TableCell>
                <TableCell className="text-right font-mono text-amber-700">{formatCurrency(totalComm)}</TableCell>
                <TableCell className="text-right font-mono text-green-700">{formatCurrency(totalDiv)}</TableCell>
                <TableCell className="text-right font-mono text-slate-600">{formatCurrency(totalRetained)}</TableCell>
                <TableCell className="text-right font-mono text-red-600">
                  {formatCurrency(
                    earningsSummary.owners.reduce((s, o) => {
                      const a = allocations[o.mortgage_agent_id] || {};
                      return s + estimateTax(a.commission || 0, a.dividend || 0).totalPersonalTax;
                    }, 0)
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            Tax estimates are approximate and based on 2025 federal + Manitoba rates. CPP contributions are estimated. Consult a tax professional for accurate filing.
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button size="lg" onClick={saveAllocations} disabled={saving} data-testid="save-allocations-btn">
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save Allocations'}
        </Button>
      </div>
    </div>
  );
}
